import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Check, ShieldCheck, Users, User } from "lucide-react";
import { API, UserContext } from "@/App";
import { toast } from "sonner";

const PLANS = {
  solo: {
    title: "Individual Pass",
    price: "$14.99",
    Icon: User,
    features: ["1 adventurer profile", "Full Treasure Tome experience", "12 months of access"],
  },
  family: {
    title: "Family Pass",
    price: "$19.99",
    regularPrice: "$24.99",
    Icon: Users,
    features: ["1 parent + up to 5 kids", "Shared-device profiles", "Family dashboard", "12 months of access"],
  },
};

export default function Billing() {
  const { account, billing, refreshBilling } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(billing);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [statusError, setStatusError] = useState(false);
  const plan = account?.account_type === "family" ? "family" : "solo";
  const info = PLANS[plan];
  const Icon = info.Icon;

  const destination = () => account?.role === "parent" ? "/parent" : account?.user_id ? "/app" : "/create-profile";
  const clearParams = () => window.history.replaceState({}, "", "/billing");

  useEffect(() => {
    let active = true;
    const initialize = async () => {
      const params = new URLSearchParams(location.search);
      if (params.get("canceled")) {
        clearParams();
        toast("Checkout canceled. You were not charged.");
      }
      if (params.get("success") && params.get("session_id")) {
        setConfirming(true);
        try {
          const { data } = await axios.post(`${API}/billing/confirm-checkout`, { session_id: params.get("session_id") });
          if (!active) return;
          setStatus(data);
          setStatusError(false);
          await refreshBilling?.();
          clearParams();
          toast.success("Your 12-month Treasure Tome pass is active!");
          navigate(destination(), { replace: true });
        } catch (error) {
          if (active) setStatusError(true);
          toast.error(error.response?.data?.detail || "We could not confirm that payment yet. Please refresh in a moment.");
        } finally {
          if (active) setConfirming(false);
        }
        return;
      }
      try {
        const { data } = await axios.get(`${API}/billing/status`);
        if (active) {
          setStatus(data);
          setStatusError(false);
        }
      } catch {
        if (active) setStatusError(true);
      }
    };
    initialize();
    return () => { active = false; };
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/billing/checkout`, { plan });
      window.location.href = data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Checkout could not be started.");
      setBusy(false);
    }
  };

  const isPaid = status?.status === "active";
  const inTrial = status?.status === "trialing";
  const familyLaunch = plan === "family" && status?.family_launch_price_available !== false;
  const displayedPrice = familyLaunch ? "$19.99" : (plan === "family" ? "$24.99" : info.price);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "linear-gradient(180deg, #B8D8E8 0%, #FFF9F0 70%)" }}>
      <div className="w-full max-w-lg">
        <div className="open-book p-7">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-3" style={{ background: "#E0F2FE", color: "#0284C7" }}><Icon className="w-7 h-7" /></div>
            <h1 className="font-heading text-2xl font-bold" style={{ color: "#4A3224" }}>{isPaid ? "Your pass is active" : inTrial ? "Your free trial is active" : "Keep your adventure going"}</h1>
            <p className="text-sm mt-2" style={{ color: "#7A5946" }}>
              {isPaid ? "You already have access. There is no automatic renewal." : inTrial ? `Enjoy all features during your 7-day trial${status?.trial_days_remaining ? ` — about ${status.trial_days_remaining} day${status.trial_days_remaining === 1 ? "" : "s"} left` : ""}. No card is needed for the trial.` : "Your trial has ended. Choose a one-time pass to continue."}
            </p>
          </div>

          <div className="tt-card p-5 mb-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-heading text-lg font-bold" style={{ color: "#4A3224" }}>{info.title}</h2>
                {familyLaunch && <span className="inline-block text-xs font-bold mt-1 px-2 py-1 rounded-full" style={{ color: "#92400E", background: "#FEF3C7" }}>Launch price for the first 100 families</span>}
              </div>
              <div className="text-right">
                {familyLaunch && <div className="text-sm line-through" style={{ color: "#9A7B68" }}>{info.regularPrice}</div>}
                <div className="font-heading text-2xl font-bold" style={{ color: "#D97706" }}>{displayedPrice}</div>
                <div className="text-xs" style={{ color: "#7A5946" }}>one payment</div>
              </div>
            </div>
            <div className="space-y-2 mt-4">
              {info.features.map(feature => <div key={feature} className="flex items-center gap-2 text-sm" style={{ color: "#4A3224" }}><Check className="w-4 h-4" style={{ color: "#16A34A" }} />{feature}</div>)}
            </div>
          </div>

          {statusError && <div className="rounded-xl p-3 mb-4 text-sm" style={{ background: "#FEE2E2", color: "#991B1B" }}>We could not load your billing status. Refresh the page or sign in again.</div>}
          {status && !status.configured && <div className="rounded-xl p-3 mb-4 text-sm" style={{ background: "#FEF3C7", color: "#92400E" }}>Payments are being connected. Your trial still works, but checkout is not ready yet.</div>}

          {isPaid ? (
            <button onClick={() => navigate(destination())} className="tt-btn tt-btn-ocean w-full py-3">Return to Treasure Tome</button>
          ) : (
            <>
              <button onClick={checkout} disabled={busy || confirming || !status?.configured} className="tt-btn tt-btn-ocean w-full py-3 disabled:opacity-40">{confirming ? "Confirming payment..." : busy ? "Opening secure checkout..." : `Buy 12 Months for ${displayedPrice}`}</button>
              {inTrial && <button onClick={() => navigate(destination())} className="w-full py-3 mt-2 text-sm font-semibold" style={{ color: "#7A5946" }}>Continue my free trial</button>}
            </>
          )}

          <div className="flex items-center justify-center gap-2 mt-5 text-xs" style={{ color: "#7A5946" }}><ShieldCheck className="w-4 h-4" />Secure checkout by Stripe · No automatic renewal</div>
          <p className="text-[11px] text-center mt-3" style={{ color: "#9A7B68" }}>By purchasing, you agree to the <button onClick={() => navigate("/terms")} className="underline">Terms</button> and acknowledge the <button onClick={() => navigate("/privacy")} className="underline">Privacy Notice</button>.</p>
        </div>
      </div>
    </div>
  );
}
